import { useZero } from "@rocicorp/zero/react";
import { useState } from "react";
import {
  type ConnectorWithOAuth,
  isItemVisible,
} from "../../../shared/connectors/getTargetValue";
import type {
  ConnectorDefinition,
  OAuthItem,
} from "../../../shared/connectors/types";
import { ExpectedError } from "../../../shared/expectedError";
import { sharedMutators } from "../../../shared/mutators";
import { unsatisfiedDependencies } from "../../lib/connector";
import { useOauthPopupWindow } from "./useOauthPopupWindow";

export type ConnectorAuthorize = ReturnType<typeof useConnectorAuthorize>;

export type PasswordGrantCredentials = { username: string; password: string };

export function useConnectorAuthorize({
  app,
  definition,
  locallySet,
  onEdit,
}: {
  app: ConnectorWithOAuth | undefined;
  definition: ConnectorDefinition | undefined;
  locallySet: ReadonlySet<string>;
  onEdit: () => void;
}) {
  const z = useZero();
  const popup = useOauthPopupWindow();
  const [isPending, setIsPending] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);

  const authorize = async (
    credentials?: PasswordGrantCredentials
  ): Promise<"success" | "cancelled" | "error"> => {
    if (!definition?.oauth || !app?.oauthConnectionId) return "cancelled";
    const oauth = definition.oauth;
    const oauthConnectionId = app.oauthConnectionId;
    setIsPending(true);
    setAuthError(null);

    const flow = async (): Promise<"success" | "cancelled" | "error"> => {
      try {
        const grantType = oauth.grantType(app.configuration);
        if (grantType === "client_credentials") {
          onEdit();
          z.mutate(
            sharedMutators.updateOAuthConnection({
              oauthConnectionId: oauthConnectionId,
            })
          );
          return "success";
        }

        if (grantType === "password") {
          if (!credentials?.username || !credentials.password) {
            setAuthError("Enter a username and password");
            return "error";
          }
          onEdit();
          z.mutate(
            sharedMutators.updateOAuthConnection({
              oauthConnectionId: oauthConnectionId,
              username: credentials.username,
              password: credentials.password,
            })
          );
          return "success";
        }

        const result = await popup.auth(oauth, app, definition.identifier);
        if (!result) return "cancelled";

        onEdit();

        // Managed flow exchanges tokens server-side; no mutator round-trip needed.
        if (!("managed" in result)) {
          z.mutate(
            sharedMutators.updateOAuthConnection({
              oauthConnectionId: oauthConnectionId,
              status: "obtained_code",
              ...result,
            })
          );
        }
        return "success";
      } catch (error) {
        // Server-reported auth failures (bad config, invalid URL, denied
        // consent) are surfaced to the user via authError; only log genuine
        // bugs so they aren't drowned out in Sentry.
        if (!(error instanceof ExpectedError)) {
          console.error("OAuth flow failed:", error);
        }
        setAuthError(
          error instanceof Error ? error.message : "Authorization failed"
        );
        return "error";
      }
    };
    return flow().finally(() => setIsPending(false));
  };

  const item = definition?.steps
    .flatMap((step) => step.items)
    .find((i): i is OAuthItem => i.kind === "oauth");
  const status = app?.oauthConnection?.status;
  const authorized = status === "connected" || status === "obtained_code";
  const remaining =
    app && definition && item
      ? unsatisfiedDependencies(app, item, definition).filter(
          (t) => !locallySet.has(`${t.kind}:${t.field}`)
        )
      : [];
  const canAuthorize =
    app != null &&
    app.authorizationMode !== "visitor" &&
    definition?.oauth != null &&
    item != null &&
    app.oauthConnectionId != null &&
    isItemVisible(item, app, definition) &&
    !authorized &&
    remaining.length === 0;

  return { authorize, isPending, authError, remaining, canAuthorize };
}
